import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Grid
} from '@mui/material';
import { Refresh as RefreshIcon, ShowChart as ShowChartIcon } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import ProjectXProjectionsChart from '../../projections_explorer/dashboard/ProjectXProjectionsChart';
import FormattedNarrative, { SectionRenderer } from './FormattedNarrative';
import DebugPanel from './DebugPanel';
import DataSourcesInfo from './DataSourcesInfo';

const THEME_COLOR = '#673ab7';

const ProjectionsSummaryTab = ({ summary, loading, onGenerate, onRegenerate, schoolName, selectedSchool, showAdminControls = true, showDataSources = true }) => {
  // Transform data format for ProjectXProjectionsChart
  const chartData = useMemo(() => {
    if (!summary?.data?.projections) return null;

    const { historical, projections } = summary.data.projections;

    // Transform historical data to actual_enrollment format
    const actual_enrollment = {};
    if (historical) {
      historical.forEach(h => {
        // Convert year to "YYYY-YYYY" format (e.g., 2023 -> "2023-2024")
        const schoolYear = `${h.year}-${h.year + 1}`;
        actual_enrollment[schoolYear] = h.by_grade || {};
      });
    }

    // Transform projections to median format with grade-level data
    const median_projections = {};
    if (projections) {
      projections.forEach(p => {
        const schoolYear = `${p.year}-${p.year + 1}`;
        // Use grade-level breakdown from backend
        median_projections[schoolYear] = p.by_grade || {};
      });
    }

    return {
      actual_enrollment,
      projections: {
        median: median_projections
      },
      based_on_user_data: false
    };
  }, [summary]);

  // Parse sections from narrative - must be before early returns
  const parsedSections = useMemo(() => {
    if (!summary) return null;
    
    const narrativeText = summary.narrative;
    if (!narrativeText || typeof narrativeText !== 'string') return null;
    
    // First try to parse as JSON
    try {
      const trimmed = narrativeText.trim();
      if (trimmed.startsWith('{')) {
        const parsed = JSON.parse(trimmed);
        if (parsed.sections && parsed.sections.length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      // Not JSON, continue to markdown parsing
    }
    
    // Parse markdown sections (## headers)
    const sections = [];
    let executiveSummary = null;
    
    const parts = narrativeText.split(/^## /gm).filter(Boolean);
    parts.forEach(part => {
      const lines = part.split('\n');
      const title = lines[0].trim();
      const content = lines.slice(1).join('\n').trim();
      
      // Check if this is an executive summary at the start
      if (title.toLowerCase().includes('executive') || title.toLowerCase().includes('summary')) {
        executiveSummary = content;
      }
      
      sections.push({ title, content });
    });
    
    return { executive_summary: executiveSummary, sections };
  }, [summary]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Generating projections summary...</Typography>
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No projections summary generated yet
        </Typography>
        <Button
          variant="contained"
          onClick={onGenerate}
          startIcon={<RefreshIcon />}
          sx={{ mt: 2 }}
        >
          Generate Projections Summary
        </Button>
      </Box>
    );
  }

  if (summary.error) {
    return (
      <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={onGenerate}>
          Retry
        </Button>
      }>
        {summary.error}
      </Alert>
    );
  }

  // Extract fields from summary (works for both live and stored+data formats)
  const { narrative, from_cache, tokens_used, mode } = summary;

  // Extract sections from parsed data
  const sections = parsedSections?.sections || [];
  
  // Helper to match section titles (case-insensitive, checks if title contains the key phrase)
  const matchTitle = (section, targetPhrase) => {
    if (!section?.title) return false;
    return section.title.toLowerCase().includes(targetPhrase.toLowerCase());
  };
  
  // Get executive summary - first from top-level field, then look in sections
  const executiveSummarySection = sections.find(s => matchTitle(s, 'executive') || matchTitle(s, 'summary'));
  const executiveSummary = parsedSections?.executive_summary || executiveSummarySection?.content;
  
  // Find specific sections by title (case-insensitive, partial match)
  const overallTrajectorySection = sections.find(s => matchTitle(s, 'trajectory'));
  const gradeLevelSection = sections.find(s => matchTitle(s, 'grade'));
  const planningSection = sections.find(s => matchTitle(s, 'planning') || matchTitle(s, 'implication'));
  
  // Get remaining sections not explicitly rendered
  const isExplicitSection = (s) => 
    matchTitle(s, 'executive') || matchTitle(s, 'summary') ||
    matchTitle(s, 'trajectory') || matchTitle(s, 'grade') || 
    matchTitle(s, 'planning') || matchTitle(s, 'implication');
  const otherSections = sections.filter(s => !isExplicitSection(s));

  return (
    <Box>
      {/* Regenerate Button */}
      {onRegenerate && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={onRegenerate}
            startIcon={<RefreshIcon />}
            size="small"
          >
            Regenerate
          </Button>
        </Box>
      )}
      
      {/* Mode Indicator - Admin only */}
      {showAdminControls && mode && (
        <Alert 
          severity={mode === 'openai' ? 'success' : (mode === 'stored' ? 'info' : 'warning')} 
          sx={{ mb: 2 }}
        >
          <strong>
            {mode === 'openai' 
              ? '✅ OpenAI API Active - Real AI Analysis' 
              : (mode === 'stored'
                  ? '📁 Loaded from Database - Fresh Data + Stored AI Narrative'
                  : '⚠️ MOCK MODE - OpenAI API Not Configured')}
          </strong>
          {mode === 'mock' && (
            <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.9em' }}>
              Using placeholder narrative for testing. Configure OpenAI API key to enable real AI analysis.
            </Box>
          )}
        </Alert>
      )}

      {/* Metadata - Admin only */}
      {showAdminControls && (
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {from_cache && (
            <Chip label="From Cache" color="info" size="small" />
          )}
          {mode === 'stored' && (
            <Chip label="Stored AI Narrative + Fresh Data" color="success" size="small" />
          )}
          {mode === 'openai' && (
            <Chip label="Live AI Generation" color="primary" size="small" />
          )}
          {tokens_used > 0 && (
            <Chip label={`${tokens_used} tokens used`} size="small" variant="outlined" />
          )}
        </Box>
      )}

      {/* Data Sources */}
      {showDataSources && summary.data_sources && (
        <DataSourcesInfo sources={[
          { label: "Historical Enrollment", value: summary.data_sources.historical_enrollment },
          { label: "Projections", value: summary.data_sources.projections },
          ...(summary.data_sources.population ? [{ label: "Population", value: summary.data_sources.population }] : [])
        ]} />
      )}

      {/* 1. Executive Summary (first) */}
      {executiveSummary && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 2,
            backgroundColor: '#ffffff',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 4,
              backgroundColor: THEME_COLOR
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5, ml: 0.5 }}>
            <Box sx={{ color: THEME_COLOR, display: 'flex', alignItems: 'center', '& svg': { fontSize: 28 } }}>
              <ShowChartIcon />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: THEME_COLOR }}>
              Executive Summary
            </Typography>
          </Box>
          <Box sx={{ 
            ml: 0.5, 
            fontSize: '1.1rem', 
            fontWeight: 500,
            '& p': { margin: 0 },
            '& strong': { fontWeight: 700 }
          }}>
            <ReactMarkdown>{executiveSummary}</ReactMarkdown>
          </Box>
        </Paper>
      )}

      {/* 2. Overall Trajectory Section with Enrollment Projections Side-by-Side */}
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
          {overallTrajectorySection ? (
            <SectionRenderer 
              section={overallTrajectorySection} 
              sx={{ mb: 0, height: '100%' }}
              showExplorerLink={!showAdminControls}
              selectedSchool={selectedSchool}
              summaryType="projections"
            />
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 0,
                height: '100%',
                backgroundColor: '#ffffff',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 4,
                  backgroundColor: THEME_COLOR
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5, ml: 0.5 }}>
                <Box sx={{ color: THEME_COLOR, display: 'flex', alignItems: 'center', '& svg': { fontSize: 28 } }}>
                  <ShowChartIcon />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: THEME_COLOR }}>
                  Overall Trajectory
                </Typography>
              </Box>
              <Typography sx={{ ml: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
                Trajectory analysis not available
              </Typography>
            </Paper>
          )}
          </Grid>
          {chartData && selectedSchool && chartData.actual_enrollment && chartData.projections && (
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 0,
                  height: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    backgroundColor: THEME_COLOR
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5, ml: 0.5 }}>
                  <Box sx={{ color: THEME_COLOR, display: 'flex', alignItems: 'center', '& svg': { fontSize: 28 } }}>
                    <ShowChartIcon />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: THEME_COLOR }}>
                    Enrollment Projections
                  </Typography>
                </Box>
                <Box sx={{ ml: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Midpoint Scenario
                  </Typography>
                  <ProjectXProjectionsChart
                    school={selectedSchool}
                    enrollmentData={chartData}
                    selectedGrades={[]}
                    timeframe="5-year"
                    view="school-level"
                    targets={{}}
                    selectedScenarios={['median']}
                  />
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>

      {/* 3. Grade-Level Analysis */}
      {gradeLevelSection && (
        <SectionRenderer 
          section={gradeLevelSection}
          showExplorerLink={!showAdminControls}
          selectedSchool={selectedSchool}
          summaryType="projections"
        />
      )}

      {/* 4. Planning Implications */}
      {planningSection && (
        <SectionRenderer 
          section={planningSection}
          showExplorerLink={!showAdminControls}
          selectedSchool={selectedSchool}
          summaryType="projections"
        />
      )}

      {/* Any other sections from AI response (excluding ones we render explicitly) */}
      {otherSections.map((section, idx) => (
        <SectionRenderer 
          key={idx} 
          section={section}
          showExplorerLink={!showAdminControls}
          selectedSchool={selectedSchool}
          summaryType="projections"
        />
      ))}

      {/* Fallback: If no sections found, render narrative via FormattedNarrative */}
      {(!parsedSections?.sections?.length) && narrative && (
        <FormattedNarrative narrative={narrative} />
      )}

        {/* Debug Panel - Admin only */}
        {showAdminControls && (
          <DebugPanel debug={summary.debug} data={summary.data} mode={mode} />
        )}
    </Box>
  );
};

export default ProjectionsSummaryTab;

